import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { MapPin } from 'lucide-react'
import { NavbarResponse, getNavbar } from '../../apis/navbar'

import Typography from '@Components/typo'
import PageLayout from '@Components/layout/pageLayout'
import { Input } from '@Components/input'
import { useState } from 'react'
import { Button, ButtonWithIcon } from '@Components/button'
import Link from 'next/link'
import { useTranslation } from 'next-i18next'
import LineIcon from '@Components/icon/line'
import IGIcon from '@Components/icon/ig'
import FacebookIcon from '@Components/icon/facebook'
import SuccessModal from '@Components/modal/status/success'
import {
  contactUsPages,
  ContactUsResponse,
  CreateContactUs,
} from '../../apis/contactUsPage'
import { openGoogleMap } from '../../utils/openGoogleMap'
import { FooterResponse, getFooter } from '../../apis/footer'
import ErrorModal from '@Components/modal/status/error'
import { Seo } from '@Components/seo'

interface ContactUsPage {
  navbar: NavbarResponse
  contactUs: {
    base: ContactUsResponse
  }
  footer: FooterResponse
}

const defaultValue = {
  name: {
    value: '',
    touch: false,
    valid: false,
  },
  email: {
    value: '',
    touch: false,
    valid: false,
  },
  tel: {
    value: '',
    touch: false,
    valid: false,
  },
  message: {
    value: '',
    touch: false,
    valid: false,
  },
}

const ContactUs = ({ navbar, contactUs, footer }: ContactUsPage) => {
  const { t } = useTranslation('common')
  const { base } = contactUs
  const [data, setData] = useState(defaultValue)

  const [isOpenSuccessModal, setIsOpenSuccessModal] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isOpenFailModal, setIsOpenFailModal] = useState(false)
  const { i18n } = useTranslation()

  const handleEmailChange = (inputEmail: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    setData({
      ...data,
      email: {
        value: inputEmail,
        valid: emailRegex.test(inputEmail),
        touch: true,
      },
    })
  }

  const handleTelChange = (inputTel: string) => {
    const telRegex =
      /^(0[0-9]{1,2}\d{3}\d{4}|[0-9]{2}\d{3}\d{4}|0[0-9]{1,2}\d{4}\d{4}|[0-9]{2}\d{4}\d{4})$/
    setData({
      ...data,
      tel: { value: inputTel, valid: telRegex.test(inputTel), touch: true },
    })
  }

  const handleSubmit = async () => {
    setData({
      name: {
        ...data.name,
        touch: true,
      },
      email: {
        ...data.email,
        touch: true,
      },
      tel: {
        ...data.tel,
        touch: true,
      },
      message: {
        ...data.message,
        touch: true,
      },
    })

    const isAllTouch =
      data.name.touch &&
      data.email.touch &&
      data.tel.touch &&
      data.message.touch

    const isAllValid =
      data.name.valid &&
      data.email.valid &&
      data.tel.valid &&
      data.message.valid

    if (isAllTouch && isAllValid) {
      setIsLoading(true)
      const submitData: CreateContactUs = {
        Name: data.name.value,
        Email: data.email.value,
        PhoneNumber: data.tel.value,
        Message: data.message.value,
      }

      await fetch('/api/postContactUs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          data: submitData,
        }),
      }).then((res) => {
        if (res.status === 200) {
          setIsOpenSuccessModal(true)
        } else {
          setIsOpenFailModal(true)
        }
        setIsLoading(false)
      })
    }
  }

  const clearAllData = () => {
    setData(defaultValue)
    setIsOpenSuccessModal(false)
    setIsOpenFailModal(false)
  }

  return (
    <>
      <Seo
        title={base.Seo?.MetaTitle}
        description={base.Seo?.MetaDescription}
        keywords={base.Seo?.Keywords}
        canonicalURL={base.Seo?.CanonicalURL || ''}
      />
      <PageLayout
        navbar={navbar}
        footer={footer}
        className='bg-neutral-level-3-default'
      >
        {isOpenSuccessModal && (
          <SuccessModal
            isOpenModal={isOpenSuccessModal}
            setIsOpenModal={clearAllData}
          >
            <Typography className='flex text-center'>
              {base.SuccessText}
            </Typography>
            <div className='w-full'>
              <Button onClick={clearAllData}>{base.SuccessTextButton}</Button>
            </div>
          </SuccessModal>
        )}

        {isOpenFailModal && (
          <ErrorModal
            isOpenModal={isOpenFailModal}
            titleModal={base.ModalFailText}
            subTitle={base.ModalFailTextTitle}
            setIsOpenModal={clearAllData}
          >
            <div className='w-full'>
              <Button onClick={clearAllData}>{base.ModalFailTextButton}</Button>
            </div>
          </ErrorModal>
        )}
        <div className='flex w-full flex-col md:flex-row'>
          <div className='flex flex w-full flex-col gap-8 rounded-2xl bg-white p-8 md:w-1/2'>
            <Typography size='h4' weight='bold'>
              {base.FormTitle}
            </Typography>

            <div className='flex flex-col gap-2'>
              <Typography>{base.LabelName}</Typography>
              <Input
                placeholder={base.PlaceholderName}
                error={
                  data.name.touch && !data.name.valid
                    ? base.ErrorMessageName
                    : ''
                }
                value={data.name.value}
                onChange={(e) => {
                  setData({
                    ...data,
                    name: {
                      value: e.target.value,
                      valid: e.target.value !== '',
                      touch: true,
                    },
                  })
                }}
              />
            </div>

            <div className='flex flex-col gap-2'>
              <Typography>{base.LabelEmail}</Typography>
              <Input
                placeholder={base.PlaceholderEmail}
                error={
                  data.email.touch && !data.email.valid
                    ? base.ErrorMessageEmail
                    : ''
                }
                value={data.email.value}
                onChange={(e) => handleEmailChange(e.target.value)}
              />
            </div>

            <div className='flex flex-col gap-2'>
              <Typography>{base.LabelPhoneNumber}</Typography>
              <Input
                placeholder={base.PlaceholderPhoneNumber}
                maxLength={10}
                error={
                  data.tel.touch && !data.tel.valid
                    ? base.ErrorMessagePhoneNumber
                    : ''
                }
                value={data.tel.value}
                onChange={(e) => handleTelChange(e.target.value)}
              />
            </div>

            <div className='flex flex-col gap-2'>
              <Typography>{base.LabelMessage}</Typography>
              <Input
                type='textarea'
                placeholder={base.PlaceholderMessage}
                error={
                  data.message.touch && !data.message.valid
                    ? base.ErrorMessageMessage
                    : ''
                }
                value={data.message.value}
                onChange={(e) => {
                  setData({
                    ...data,
                    message: {
                      value: e.target.value,
                      valid: e.target.value !== '',
                      touch: true,
                    },
                  })
                }}
              />
            </div>

            <div className='flex flex-col gap-1'>
              <div className='flex flex-row gap-2'>
                <span
                  className='prose text-content-default'
                  dangerouslySetInnerHTML={{
                    __html: t('contactUs.consent'),
                  }}
                />
              </div>
            </div>

            <div>
              <Button isLoading={isLoading} onClick={handleSubmit}>
                {base.SubmitTextButton}
              </Button>
            </div>
          </div>

          <div className='flex flex w-full flex-col gap-8 rounded-2xl p-8 md:w-1/2'>
            <Typography size='h4' weight='bold'>
              {base.ContactUsTitle}
            </Typography>
            <div className='flex flex-col gap-2'>
              <Typography size='small' weight='bold'>
                {base.ContactUsLabelAddress}
              </Typography>
              <Typography>{base.ContactUsAddress}</Typography>
              <ButtonWithIcon
                size='md'
                variant='secondary'
                onClick={() => openGoogleMap(base.Latitude, base.Longitude)}
                rightIcon={<MapPin className='h-icon-md w-icon-md' />}
              >
                {base.LabelMapButton}
              </ButtonWithIcon>
            </div>

            <div className='flex flex-col gap-2'>
              <Typography size='small' weight='bold'>
                {base.ContactUsLabelPhone}
              </Typography>
              <div
                className='cursor-pointer'
                onClick={() => window.open(`tel:+66${base.ContactUsPhone}`)}
              >
                <Typography color='text-link-default'>
                  {base.ContactUsPhone}
                </Typography>
              </div>
            </div>

            <div className='flex flex-col gap-2'>
              <Typography size='small' weight='bold'>
                {base.ContactUsLabelEmail}
              </Typography>
              <Link href={`mailto:${base.ContactUsEmail}`} target='_blank'>
                <Typography color='text-link-default'>
                  {base.ContactUsEmail}
                </Typography>
              </Link>
            </div>

            <div className='flex flex-row flex-wrap gap-3'>
              <Link
                href={base.FacebookLink || ''}
                target='_blank'
                locale={i18n.language}
                aria-label='facebook'
              >
                <ButtonWithIcon
                  size='md'
                  variant='secondary'
                  leftIcon={<FacebookIcon />}
                >
                  {t('contactUs.facebook')}
                </ButtonWithIcon>
              </Link>
              <Link
                href={base.LineLink || ''}
                target='_blank'
                locale={i18n.language}
                aria-label='line'
              >
                <ButtonWithIcon
                  size='md'
                  variant='secondary'
                  leftIcon={<LineIcon />}
                >
                  {t('contactUs.line')}
                </ButtonWithIcon>
              </Link>
              <Link
                href={base.IGLink || ''}
                target='_blank'
                locale={i18n.language}
                aria-label='instagram'
              >
                <ButtonWithIcon
                  size='md'
                  variant='secondary'
                  leftIcon={<IGIcon />}
                >
                  {t('contactUs.ig')}
                </ButtonWithIcon>
              </Link>
            </div>
          </div>
        </div>
      </PageLayout>
    </>
  )
}

export default ContactUs

export const getServerSideProps = async ({
  locale = 'en',
}: {
  locale: string
}) => {
  const { getContactUsPages } = contactUsPages
  const [navbar, base, footer] = await Promise.all([
    getNavbar({ locale }),
    getContactUsPages({ locale }),
    getFooter({ locale }),
  ])

  return {
    props: {
      ...(await serverSideTranslations(locale, ['common'])),
      navbar: navbar[0],
      footer: footer[0],
      contactUs: {
        base: {
          ...base[0],
        },
      },
    },
  }
}
